import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpContext, HttpHeaders } from '@angular/common/http';
import { backendEndPointToken, hardCodedHospitalId, terminologyCanonicalUrlBaseToken } from '../../app.config';
import { AuthService } from '../../shared/auth/auth.service';
import { UtilityService } from '../../shared/utility.service';
import { FhirResourceService } from '../../shared/fhir-resource.service';
import { StateService } from '../../shared/state.service';
import { BundleEntry, ChargeItem, ChargeItemDefinition, CommunicationRequest, Dosage, Encounter, Identifier, Invoice, Medication, MedicationDispense, Patient, Reference } from 'fhir/r4';
import { Observable, forkJoin, map, of, switchMap, throwError } from 'rxjs';
import { LoadingUIEnabled } from '../../loading.interceptor';
import { ReferenceDisplayService } from '../../shared/reference-display.service';
import { catchError } from 'rxjs/operators';

export interface WalkInPatientPayload {
    name: string;
    birthDate?: string | null;
    gender?: 'male' | 'female' | 'other' | 'unknown' | '' | null;
}

export interface DosageDraftForm {
    dosageInstruction?: string;
    dosageTiming?: {
        timingCode?: { code?: string; display?: string; system?: string } | string;
        duration?: number | string;
        durationUnit?: string;
    };
    doseAndRateDoseQuantity?: {
        value?: number | string;
        unit?: string;
    };
}

export interface PendingDispenseDraft {
    id: string;
    medication: Medication;
    quantity: number;
    dosageDraft: DosageDraftForm;
}

interface PatientResolution {
    reference: Reference;
    patient: Patient;
}


interface EncounterBundleContext {
    reference: Reference;
    entry: BundleEntry;
}

interface PreparedRowEntries {
    entries: BundleEntry[];
    displayLabel: string;
}



type InvoiceLineItemPriceComponentArray = NonNullable<NonNullable<Invoice['lineItem']>[number]['priceComponent']>;
type InvoiceTotalPriceComponentArray = NonNullable<Invoice['totalPriceComponent']>;
type InvoiceComponentType = InvoiceLineItemPriceComponentArray[number]['type'];
type PatientHumanName = NonNullable<Patient['name']>[number];

const PRICE_COMPONENT_TYPES: ReadonlyArray<InvoiceComponentType> = ['base', 'surcharge', 'tax', 'deduction', 'discount', 'informational'];

export const buildPharmacyWalkInTag = (terminologyBase: string) => ({
    system: `${terminologyBase}/CodeSystem/PatientOrigin`,
    code: 'pharmacy-walkin',
    display: 'Pharmacy Walk-In'
} as const);

export const buildPharmacyWalkInTagQuery = (terminologyBase: string) => {
    const tag = buildPharmacyWalkInTag(terminologyBase);
    return `${tag.system}|${tag.code}`;
};

@Injectable({ providedIn: 'root' })
export class NewMedicationDispenseService {
    private readonly http = inject(HttpClient);
    private readonly backendEndPoint = inject(backendEndPointToken);
    private readonly terminologyEndpoint = inject(terminologyCanonicalUrlBaseToken);
    private readonly pharmacyWalkInTag = buildPharmacyWalkInTag(this.terminologyEndpoint);
    private readonly authService = inject(AuthService);
    private readonly utilityService = inject(UtilityService);
    private readonly fhirResourceService = inject(FhirResourceService);
    private readonly stateService = inject(StateService);
    private readonly refDisplayService = inject(ReferenceDisplayService);



    submitDispenses(patientPayload: WalkInPatientPayload, rows: PendingDispenseDraft[]): Observable<void> {
        if (!rows?.length) {
            return throwError(() => new Error('Add at least one medication before submitting.'));
        }

        return this.ensurePatient(patientPayload).pipe(
            switchMap((patientResolution) => {
                const encounterContext = this.buildEncounterEntry(patientResolution.reference);
                return this.prepareRows(rows, patientResolution.reference, encounterContext.reference).pipe(
                    switchMap((rowEntries) => {
                        if (!rowEntries.length) {
                            return throwError(() => new Error('Unable to prepare dispense bundle.'));
                        }
                        const bundleEntries = [encounterContext.entry, ...rowEntries];
                        this.logBundleDiagnostics(bundleEntries);
                        const bundle = {
                            resourceType: 'Bundle',
                            type: 'transaction',
                            entry: bundleEntries
                        } as const;
                        return this.fhirResourceService.postBundle(bundle).pipe(map(() => void 0));
                    })
                );
            })
        );
    }

    ensurePatient(payload: WalkInPatientPayload): Observable<PatientResolution> {
        const normalizedName = (payload.name ?? '').trim();
        if (!normalizedName) {
            return throwError(() => new Error('Patient name is required.'));
        }

        const normalizedBirthDate = this.normalizeDate(payload.birthDate);
        const normalizedGender = this.normalizeGender(payload.gender);

        const queryParts = [`name=${encodeURIComponent(normalizedName)}`, '_count=5'];
        if (normalizedBirthDate) {
            queryParts.push(`birthdate=${encodeURIComponent(normalizedBirthDate)}`);
        }
        if (normalizedGender) {
            queryParts.push(`gender=${encodeURIComponent(normalizedGender)}`);
        }

        const searchUrl = `${this.backendEndPoint}/Patient?${queryParts.join('&')}`;

        return this.http.get<{ entry?: Array<{ resource: Patient }> }>(searchUrl).pipe(
            switchMap((bundle) => {
                // Find a patient that strictly matches the provided criteria, including missing fields
                const existing = (bundle.entry || [])
                    .map(e => e.resource)
                    .find(p => {
                        const pGender = this.normalizeGender(p.gender);
                        const pDob = this.normalizeDate(p.birthDate);
                        
                        // If input has gender/dob, it must match. If input is missing them, patient must also be missing them.
                        const genderMatches = normalizedGender ? pGender === normalizedGender : !pGender;
                        const dobMatches = normalizedBirthDate ? pDob === normalizedBirthDate : !pDob;

                        return genderMatches && dobMatches;
                    });

                if (existing?.id) {
                    return of({
                        reference: { reference: `Patient/${existing.id}`, display: existing.name?.[0]?.text || normalizedName },
                        patient: existing
                    });
                }
                return this.createPatient(payload).pipe(
                    map((patient) => ({
                        reference: { reference: `Patient/${patient.id}`, display: patient.name?.[0]?.text || normalizedName },
                        patient
                    }))
                );
            })
        );
    }

    private createPatient(payload: WalkInPatientPayload): Observable<Patient> {
        const currentUser = this.authService.user.getValue();
        const practitionerRef = this.stateService.getPractitionerReference(currentUser?.fhirResourceId);
        const normalizedGender = this.normalizeGender(payload.gender);
        const normalizedBirthDate = this.normalizeDate(payload.birthDate);

        let sidObs = of('00000');
        if (practitionerRef?.reference) {
            sidObs = this.refDisplayService.ensureIdentifier(practitionerRef.reference).pipe(
                map(sid => {
                    if (!sid || sid === practitionerRef.reference?.split("/").pop()) return '00000';
                    return sid.split('-').pop() || '00000';
                }),
                catchError(() => of('00000'))
            );
        }

        return sidObs.pipe(
            switchMap(sidPart => {
                const now = new Date();
                const yy = String(now.getFullYear()).slice(-2);
                const mm = String(now.getMonth() + 1).padStart(2, '0');
                const dd = String(now.getDate()).padStart(2, '0');
                const hh = String(now.getHours()).padStart(2, '0');
                const min = String(now.getMinutes()).padStart(2, '0');
                const ss = String(now.getSeconds()).padStart(2, '0');
                const walkInId = `WALKIN-${sidPart}-${yy}${mm}${dd}-${hh}${min}${ss}`;

                const patientResource: Patient = {
                    resourceType: 'Patient',
                    identifier: [{
                        system: 'https://elikita-server.daalitech.com/fhir/identifier/pid',
                        value: walkInId
                    }],
                    meta: {
                        tag: [this.pharmacyWalkInTag]
                    },
                    name: [this.buildHumanName(payload.name)],
                    ...(normalizedGender ? { gender: normalizedGender } : {}),
                    ...(normalizedBirthDate ? { birthDate: normalizedBirthDate } : {}),
                    generalPractitioner: practitionerRef ? [practitionerRef] : undefined,
                    managingOrganization: { reference: `Organization/${currentUser?.organizationId}` },
                    active: true
                };

                return this.http.post<Patient>(`${this.backendEndPoint}/Patient`, patientResource, this.fhirHeaders());
            })
        );
    }

    private buildEncounterEntry(patientRef: Reference): EncounterBundleContext {
        const currentUser = this.authService.user.getValue();
        const practitioner = this.stateService.getPractitionerReference(currentUser?.fhirResourceId);
        const encounter: Encounter = {
            resourceType: 'Encounter',
            status: 'in-progress',
            class: {
                system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
                code: 'AMB',
                display: 'ambulatory'
            },
            type: [
                {
                    coding: [
                        {
                            system: 'http://terminology.hl7.org/CodeSystem/encounter-type',
                            code: 'PHARM',
                            display: 'Pharmacy visit'
                        }
                    ],
                    text: 'Pharmacy visit'
                }
            ],
            subject: patientRef,
            participant: practitioner
                ? [
                    {
                        individual: practitioner,
                        type: [{ text: 'Pharmacist' }]
                    }
                ]
                : undefined,
            period: {
                start: new Date().toISOString()
            },
            serviceProvider: {
                reference: `Organization/${currentUser?.organizationId}`
            }
        };

        const fullUrl = this.randomFullUrl('encounter');
        return {
            entry: {
                fullUrl,
                resource: encounter,
                request: { method: 'POST', url: 'Encounter' }
            },
            reference: { reference: fullUrl }
        };
    }

    private prepareRows(
        rows: PendingDispenseDraft[],
        patientRef: Reference,
        encounterRef: Reference
    ): Observable<BundleEntry[]> {
        const performer = this.stateService.getPractitionerReference(this.authService.user.getValue()?.fhirResourceId);
        if (!performer) {
            return throwError(() => new Error('Logged-in user is missing a linked Practitioner profile.'));
        }

        return forkJoin(
            rows.map((row) => this.prepareSingleRow(row, patientRef, encounterRef, performer))
        ).pipe(
            map((results) => results.flatMap((r) => r.entries))
        );
    }

    private prepareSingleRow(
        row: PendingDispenseDraft,
        patientRef: Reference,
        encounterRef: Reference,
        performer: Reference
    ): Observable<PreparedRowEntries> {
        const medicationId = row.medication?.id;
        if (!medicationId) {
            return throwError(() => new Error('Medication is missing an id.'));
        }
        const normalizedQuantity = Number(row.quantity);
        if (!Number.isFinite(normalizedQuantity) || normalizedQuantity <= 0) {
            return throwError(() => new Error('Quantity must be greater than zero for all medications.'));
        }
        console.log('[tracing dispense quantity]', normalizedQuantity, 'for medication', medicationId, row.medication?.code?.text);

        return forkJoin({
            medication: this.http.get<Medication>(`${this.backendEndPoint}/Medication/${medicationId}`),
            chargeItemDefinition: this.fetchChargeItemDefinition(medicationId)
        }).pipe(
            map(({ medication, chargeItemDefinition }) =>
                this.buildEntriesForRow({
                    draft: row,
                    patientRef,
                    encounterRef,
                    performer,
                    medication,
                    chargeItemDefinition,
                    quantity: normalizedQuantity
                })
            )
        );
    }

    private buildEntriesForRow(args: {
        draft: PendingDispenseDraft;
        patientRef: Reference;
        encounterRef: Reference;
        performer: Reference;
        medication: Medication;
        chargeItemDefinition: ChargeItemDefinition | null;
        quantity: number;
    }): PreparedRowEntries {
        const { medication, chargeItemDefinition, quantity, patientRef, performer, encounterRef, draft } = args;
        const medReference: Reference = { reference: `Medication/${medication.id}`, display: medication.code?.text };
        const dosage = this.utilityService.constructDosageTypeObject(draft.dosageDraft);
        if (!dosage) {
            throw new Error('Every medication requires a dosage definition.');
        }

        const remainingExt = this.findInventoryExtension(medication);
        const numericRemaining = remainingExt ? this.parseRemainingQuantity(remainingExt) : null;
        console.log('[tracing dispense quantity]', quantity, 'for medication', medication.id, 'to be deducted from remaining stock', numericRemaining);
        if (numericRemaining !== null && quantity > numericRemaining) {
            throw new Error(`Requested quantity (${quantity}) exceeds remaining stock (${numericRemaining}).`);
        }

        const unit = this.getInventoryUnit(medication);
        const lowStockConfig = this.utilityService.getMedicationLowStockConfig(medication);
        const dispenseFullUrl = this.randomFullUrl('medication-dispense');
        const chargeItemFullUrl = this.randomFullUrl('charge-item');
        const invoiceFullUrl = this.randomFullUrl('invoice');

        const medicationDispense: MedicationDispense = {
            resourceType: 'MedicationDispense',
            status: 'in-progress',
            subject: patientRef,
            context: encounterRef,
            performer: [{ actor: performer }],
            medicationReference: medReference,
            quantity: {
                value: quantity,
                ...(unit ? { unit, system: 'http://unitsofmeasure.org' } : {})
            },
            dosageInstruction: [dosage],
            whenPrepared: new Date().toISOString(),
            whenHandedOver: new Date().toISOString(),
            receiver: [patientRef]
        };

        const entries: BundleEntry[] = [
            {
                fullUrl: dispenseFullUrl,
                resource: medicationDispense,
                request: { method: 'POST', url: 'MedicationDispense' }
            }
        ];
        //every params going ito next funcion
console.log(chargeItemDefinition,medication,quantity,patientRef,encounterRef,dispenseFullUrl,chargeItemFullUrl,invoiceFullUrl)
// debugger;
        const chargeInvoice = this.buildBillingResources({
            chargeItemDefinition,
            medication,
            quantity,
            patientRef,
            encounterRef,
            dispenseFullUrl,
            chargeItemFullUrl,
            invoiceFullUrl
        });

        if (chargeInvoice.chargeItem) {
            entries.push(chargeInvoice.chargeItem);
        }
        if (chargeInvoice.invoice) {
            entries.push(chargeInvoice.invoice);
        }

        const remainingAfter = numericRemaining !== null ? Math.max(0, numericRemaining - quantity) : null;
        console.log('[tracing dispense quantity]', quantity, 'for medication', medication.id, 'remaining after dispense will be', remainingAfter);
        const communicationEntry = this.buildLowStockCommunicationEntry({
            medication,
            patientRef,
            encounterRef,
            performer,
            remainingAfter,
            remainingBefore: numericRemaining,
            quantity,
            unit,
            lowStockConfig
        });

        if (communicationEntry) {
            entries.push(communicationEntry);
        }

        return { entries, displayLabel: medicationDispense.medicationReference?.display || medReference.reference || draft.id };
    }

    private buildBillingResources(args: {
        chargeItemDefinition: ChargeItemDefinition | null;
        medication: Medication;
        quantity: number;
        patientRef: Reference;
        encounterRef: Reference;
        dispenseFullUrl: string;
        chargeItemFullUrl: string;
        invoiceFullUrl: string;
    }): { chargeItem?: BundleEntry; invoice?: BundleEntry } {
        const { chargeItemDefinition, medication, quantity, patientRef, encounterRef, dispenseFullUrl, chargeItemFullUrl, invoiceFullUrl } = args;
        if (!chargeItemDefinition) {
            console.warn('[NewMedicationDispenseService] Missing ChargeItemDefinition for medication', medication?.id || medication?.code?.text);
            return {};
        }

        const nowIso = new Date().toISOString();
        const currency = chargeItemDefinition?.propertyGroup?.[0]?.priceComponent?.[0]?.amount?.currency || 'NGN';
        const amountHelper = (value: number) => ({ value, currency });
        const organizationId = this.authService.getOrganizationId();
        const performingOrganization = organizationId ? { reference: `Organization/${organizationId}` } : undefined;

        const componentTotals: Record<string, number> = {
            base: 0,
            surcharge: 0,
            tax: 0,
            deduction: 0,
            discount: 0,
            informational: 0
        };

        (chargeItemDefinition.propertyGroup?.[0]?.priceComponent ?? []).forEach((component) => {
            const typeKey = (component.type ?? '').toString();
            if (!typeKey) {
                return;
            }
            const componentValue = Number(component.amount?.value ?? 0) * quantity;
            if (!Number.isFinite(componentValue)) {
                return;
            }
            componentTotals[typeKey] = (componentTotals[typeKey] || 0) + componentValue;
        });

        const totalNetValue = (componentTotals['base'] || 0)
            + (componentTotals['surcharge'] || 0)
            - (componentTotals['deduction'] || 0)
            - (componentTotals['discount'] || 0);
        const totalGrossValue = totalNetValue + (componentTotals['tax'] || 0);

        const lineItemPriceComponent: InvoiceLineItemPriceComponentArray = [];
        for (const component of chargeItemDefinition.propertyGroup?.[0]?.priceComponent ?? []) {
            const componentValue = Number(component.amount?.value ?? 0) * quantity;
            if (!Number.isFinite(componentValue) || componentValue === 0) {
                continue;
            }
            lineItemPriceComponent.push({
                type: this.coercePriceComponentType(component.type),
                code: component.code,
                factor: component.factor,
                amount: amountHelper(componentValue)
            });
        }

        const totalPriceComponent: InvoiceTotalPriceComponentArray = [];
        (Object.keys(componentTotals) as Array<keyof typeof componentTotals>).forEach((typeKey) => {
            const value = componentTotals[typeKey];
            if (!value) {
                return;
            }
            totalPriceComponent.push({
                type: this.coercePriceComponentType(typeKey),
                amount: amountHelper(value)
            });
        });

        const chargeItem: ChargeItem = {
            resourceType: 'ChargeItem',
            status: 'billed',
            subject: patientRef,
            service: [{ reference: dispenseFullUrl }],
            quantity: { value: quantity },
            occurrenceDateTime: nowIso,
            context: encounterRef,
            code: {
                coding: [
                    {
                        code: 'MED-DISP',
                        system: `${this.terminologyEndpoint}`,
                        display: 'Medication Dispense Charge'
                    }
                ],
                text: 'Medication Dispense Charge'
            },
            definitionCanonical: [`${this.backendEndPoint}/ChargeItemDefinition/${chargeItemDefinition.id}`],
            productReference: medication.id ? { reference: `Medication/${medication.id}` } : undefined,
            identifier: [this.buildChargeIdentifier()],
            ...(performingOrganization ? { performingOrganization } : {})
        };

        const chargeItemEntry: BundleEntry = {
            fullUrl: chargeItemFullUrl,
            resource: chargeItem,
            request: { method: 'POST', url: 'ChargeItem' }
        };

        const invoice: Invoice = {
            resourceType: 'Invoice',
            status: 'issued',
            subject: patientRef,
            date: nowIso,
            lineItem: [
                {
                    sequence: 1,
                    chargeItemReference: { reference: chargeItemFullUrl },
                    ...(lineItemPriceComponent.length ? { priceComponent: lineItemPriceComponent } : {})
                }
            ],
            ...(totalPriceComponent.length ? { totalPriceComponent } : {}),
            ...(Number.isFinite(totalNetValue) ? { totalNet: amountHelper(Math.max(0, totalNetValue)) } : {}),
            ...(Number.isFinite(totalGrossValue) ? { totalGross: amountHelper(Math.max(0, totalGrossValue)) } : {}),
            issuer: { reference: `Organization/${this.authService?.user.getValue()?.organizationId}` }
        };

        const invoiceEntry: BundleEntry = {
            fullUrl: invoiceFullUrl,
            resource: invoice,
            request: { method: 'POST', url: 'Invoice' }
        };

        return { chargeItem: chargeItemEntry, invoice: invoiceEntry };
    }

    private fetchChargeItemDefinition(medicationId: string): Observable<ChargeItemDefinition | null> {
        return this.utilityService.fetchAll<ChargeItemDefinition>({
            resourceType: 'ChargeItemDefinition',
            queries: { status: 'active' },
            checkOrgWideState: true,
            updateOrgWideState: true,
            localFilters: [{ key: 'status', value: 'active' }]
        }).pipe(
            map((all) => {
                const resource = all.find((r) =>
                    r.instance?.some((instance) => instance.reference === `Medication/${medicationId}`)
                    && r.status === 'active'
                );
                return resource ?? null;
            }),
            catchError((e) => {
                console.error('Error fetching charge item definition:', e);
                return of(null);
            })
        );
    }

    searchMedications(term?: string | null): Observable<Medication[]> {
        const cleaned = (term ?? '').toString().trim();
        const queries: any = { status: 'active' };
        if (cleaned) {
            queries['code:text'] = cleaned;
        }
        
        return this.utilityService.fetchAll<Medication>({
            resourceType: 'Medication',
            queries,
            checkOrgWideState: false,
            updateOrgWideState: false,
            context: new HttpContext().set(LoadingUIEnabled, false)
        }).pipe(
            map((all) => {
                const entries = all.map(resource => ({ resource }));
                const availableEntries = this.utilityService.filterAvailableMedications(entries as BundleEntry<any>[]);
                return availableEntries
                    .map((entry) => entry.resource)
                    .filter((resource): resource is Medication => Boolean(resource));
            })
        );
    }

    private logBundleDiagnostics(entries: BundleEntry[]): void {
        try {
            const resourceTypes = entries.map((entry) => entry.resource?.resourceType ?? 'Unknown');
            console.debug('[NewMedicationDispenseService] bundle entries:', entries.length, resourceTypes);
        } catch {
            // Swallow logging issues; diagnostics should never break submission.
        }
    }

    private parseRemainingQuantity(extension: any): number | null {
        const value = extension?.valueQuantity?.value ?? extension?.valueInteger ?? extension?.valueString;
        if (value === undefined || value === null || value === '') {
            return null;
        }
        const numeric = Number(value);
        return Number.isFinite(numeric) ? numeric : null;
    }

    private findInventoryExtension(medication: Medication): any | undefined {
        return medication?.extension?.find((ext: any) => typeof ext.url === 'string' && ext.url.endsWith('totalRemaining'));
    }

    private getInventoryUnit(medication: Medication): string | undefined {
        const dispensable = medication?.extension?.find((ext: any) => typeof ext.url === 'string' && ext.url.endsWith('dispensableUnits'));
        if (dispensable?.valueString) {
            return dispensable.valueString;
        }
        const totalRemaining = medication?.extension?.find((ext: any) => typeof ext.url === 'string' && ext.url.endsWith('totalRemaining'));
        return totalRemaining?.valueQuantity?.unit ?? totalRemaining?.valueString;
    }

    private buildLowStockCommunicationEntry(args: {
        medication: Medication;
        patientRef: Reference;
        encounterRef: Reference;
        performer: Reference;
        remainingAfter: number | null;
        remainingBefore: number | null;
        quantity: number;
        unit?: string;
        lowStockConfig?: { threshold?: number; unit?: string; alertsEnabled?: boolean } | null;
    }): BundleEntry | null {
        if (!this.utilityService.shouldSendLowStockAlert(args.remainingAfter, args.lowStockConfig || undefined)) {
            return null;
        }

        const currentUser = this.authService.user.getValue();
        const organizationId = currentUser?.organizationId || hardCodedHospitalId;
        if (!organizationId) {
            return null;
        }

        const numberFormatter = new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 });
        const medicationLabel = args.medication.code?.text
            || args.medication.code?.coding?.[0]?.display
            || args.medication.code?.coding?.[0]?.code
            || `Medication/${args.medication.id}`;
        const unitText = args.lowStockConfig?.unit || args.unit || '';
        const remainingText = args.remainingAfter !== null && args.remainingAfter !== undefined
            ? numberFormatter.format(Math.max(0, args.remainingAfter))
            : 'unknown';
        const thresholdText = args.lowStockConfig?.threshold !== undefined
            ? numberFormatter.format(Math.max(0, args.lowStockConfig.threshold))
            : 'configured threshold';
        const quantityText = numberFormatter.format(args.quantity);

        const payloadText = `Low stock alert: ${medicationLabel} will have ${remainingText}${unitText ? ` ${unitText}` : ''} `
            + `after dispensing ${quantityText}${unitText ? ` ${unitText}` : ''}.`
            + (args.lowStockConfig?.threshold !== undefined
                ? ` Threshold: ${thresholdText}${unitText ? ` ${unitText}` : ''}.`
                : '');

        const communication: CommunicationRequest = {
            resourceType: 'CommunicationRequest',
            status: 'active',
            priority: 'urgent',
            subject: args.patientRef,
            encounter: args.encounterRef,
            requester: args.performer,
            authoredOn: new Date().toISOString(),
            recipient: [{ reference: `Organization/${organizationId}` }],
            category: [
                {
                    coding: [
                        {
                            system: 'http://terminology.hl7.org/CodeSystem/communication-category',
                            code: 'alert',
                            display: 'Alert'
                        }
                    ],
                    text: 'Inventory'
                }
            ],
            payload: [{ contentString: payloadText.trim() }],
            ...(args.medication.id ? { about: [{ reference: `Medication/${args.medication.id}` }] } : {}),
            note: args.remainingBefore !== null
                ? [{ text: `Previous remaining: ${numberFormatter.format(Math.max(0, args.remainingBefore))}${unitText ? ` ${unitText}` : ''}` }]
                : undefined
        };

        return {
            fullUrl: this.randomFullUrl('communication-request'),
            resource: communication,
            request: { method: 'POST', url: 'CommunicationRequest' }
        };
    }

    private randomFullUrl(prefix: string): string {
        if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
            return `urn:uuid:${prefix}-${crypto.randomUUID()}`;
        }
        return `urn:uuid:${prefix}-${Math.random().toString(36).slice(2)}`;
    }

    private buildHumanName(rawName: string): PatientHumanName {
        const trimmed = (rawName ?? '').trim();
        const tokens = trimmed.split(/\s+/).filter(Boolean);
        if (!tokens.length) {
            return { text: trimmed } as PatientHumanName;
        }
        const clone = [...tokens];
        const family = clone.length > 1 ? clone.pop() : clone[0];
        return {
            text: trimmed,
            family: family || trimmed,
            ...(clone.length ? { given: clone } : {})
        };
    }

    private coercePriceComponentType(input: string | undefined | null): InvoiceComponentType {
        const normalized = (input ?? '').toLowerCase() as InvoiceComponentType;
        return PRICE_COMPONENT_TYPES.includes(normalized) ? normalized : 'informational';
    }

    private buildChargeIdentifier(): Identifier {
        return this.utilityService.buildIdentifier({
            system: `${this.terminologyEndpoint}/ChargeItem/identifier`,
            resourceType: 'ChargeItem',
            typeCoding: {
                code: 'MED-DISP',
                system: `${this.terminologyEndpoint}`,
                display: 'Medication Dispense Charge'
            },
            assigner: {
                reference: 'Organization/E-Likita',
                display: 'E-Likita Health'
            }
        });
    }

    private fhirHeaders() {
        return {
            headers: new HttpHeaders({
                'Content-Type': 'application/fhir+json',
                Accept: 'application/fhir+json',
                Prefer: 'return=representation'
            })
        };
    }

    private normalizeGender(input?: string | null): Patient['gender'] | undefined {
        const value = (input ?? '').toString().trim().toLowerCase();
        if (!value) {
            return undefined;
        }
        if (['male', 'female', 'other', 'unknown'].includes(value)) {
            return value as Patient['gender'];
        }
        return undefined;
    }

    private normalizeDate(input?: string | null): string | undefined {
        const value = (input ?? '').toString().trim();
        return value || undefined;
    }
}
